import { Briefcase, Building2, Edit3, List, Plus, RefreshCw, Users } from "lucide-react";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import AdminPageLayout from "../../components/AdminPageLayout";
import ProviderCreateWizard from "../../components/ProviderCreateWizard";
import ProviderModal from "../../components/ProviderModal";
import ResponsiveActionButton from "../../components/ResponsiveActionButton";
import { useAuth } from "../../contexts/AuthContext";
import { useTheme } from "../../contexts/ThemeContext";
import httpService from "../../services/httpService";
import { Provider } from "../../types/provider";

const ProvidersPage: React.FC = () => {
  const { theme } = useTheme();
  const { user } = useAuth();
  const navigate = useNavigate();
  const isDarkMode = theme === "dark";

  // Check if user has admin role
  const userRoles = user?.userRoles?.map(ur => ur.role) || [];
  const hasRole = (role: string) => userRoles.includes(role);
  const isAdmin = hasRole("ADMIN");
  const [providers, setProviders] = useState<Provider[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [modalMode, setModalMode] = useState<"create" | "edit">("create");
  const [selectedProvider, setSelectedProvider] = useState<Provider | null>(null);
  const [imageErrors, setImageErrors] = useState<Set<string>>(new Set());

  useEffect(() => {
    fetchProviders();
  }, []);

  const fetchProviders = async () => {
    try {
      setLoading(true);
      const response = await httpService.get<{ providers: Provider[] }>("/providers");
      setProviders(response.data.providers || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : "An error occurred");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateProvider = async (providerData: Partial<Provider>) => {
    const response = await httpService.post("/providers", {
      name: providerData.name,
    });

    // Refresh providers list
    await fetchProviders();

    // Return the created provider for logo upload
    return response.data.provider;
  };

  const handleEditProvider = async (providerData: Partial<Provider>) => {
    if (!selectedProvider) return;

    // Forward `data` only when the modal included it: present-with-value writes,
    // present-and-null clears, absent leaves the column untouched (PWA-2335).
    const body: Partial<Pick<Provider, "name" | "data">> = { name: providerData.name };
    if ("data" in providerData) body.data = providerData.data;

    await httpService.put(`/providers/${selectedProvider.id}`, body);

    // Refresh providers list
    await fetchProviders();
  };

  const handleDeleteProvider = async (providerId: string) => {
    await httpService.delete(`/providers/${providerId}`);

    // Refresh providers list
    await fetchProviders();
  };

  // Get provider logo URL with optional cache-busting
  const getProviderLogoUrl = (provider: Provider) => {
    if (!provider.logoUrl) return "/favicon.png"; // Use default favicon as placeholder
    // Cache-bust to avoid stale GCS cached content after upload
    const sep = provider.logoUrl.includes("?") ? "&" : "?";
    return `${provider.logoUrl}${sep}v=${Date.now()}`;
  };

  const openCreateWizard = () => {
    setWizardOpen(true);
  };

  const openEditModal = (provider: Provider) => {
    setSelectedProvider(provider);
    setModalMode("edit");
    setModalOpen(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <div
            className={`animate-spin rounded-full h-12 w-12 border-b-2 ${
              isDarkMode ? "border-rp-amber" : "border-blue-600"
            } mx-auto`}
          ></div>
          <p className={`mt-4 ${isDarkMode ? "text-rp-grey/50" : "text-gray-600"}`}>Loading providers...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <div className={`text-xl mb-4 ${isDarkMode ? "text-red-400" : "text-red-600"}`}>⚠️ Error</div>
          <p className={isDarkMode ? "text-rp-grey/50" : "text-gray-600"}>{error}</p>
          <button
            onClick={fetchProviders}
            className={`mt-4 px-4 py-2 rounded transition-colors ${
              isDarkMode ? "bg-blue-600 text-rp-grey hover:bg-blue-700" : "bg-blue-600 text-white hover:bg-blue-700"
            }`}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <AdminPageLayout
      title="Providers"
      description="Manage investment providers and performance metrics"
      actions={
        <>
          <ResponsiveActionButton
            label="Refresh"
            icon={<RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />}
            onClick={fetchProviders}
            disabled={loading}
            variant="default"
          />
          <ResponsiveActionButton
            label="Create Provider"
            icon={<Plus className="w-4 h-4" />}
            onClick={openCreateWizard}
            variant="primary"
          />
        </>
      }
    >
      {/* Stats Card */}
      <div className="grid grid-cols-1 md:grid-cols-1 gap-6 mb-8">
        <div className={`${isDarkMode ? "bg-rp-surface" : "bg-white"} rounded-lg shadow p-6`}>
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center">
                <Building2 className="text-white w-4 h-4" />
              </div>
            </div>
            <div className="ml-4">
              <p className={`text-sm font-medium ${isDarkMode ? "text-rp-grey/50" : "text-gray-500"}`}>Total Providers</p>
              <p className={`text-2xl font-semibold ${isDarkMode ? "text-rp-grey" : "text-gray-900"}`}>
                {providers.length}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Providers Table */}
      <div className={`${isDarkMode ? "bg-rp-surface" : "bg-white"} shadow rounded-lg`}>
        <div className={`px-6 py-4 border-b ${isDarkMode ? "border-rp-separator" : "border-gray-200"}`}>
          <h3 className={`text-lg font-medium ${isDarkMode ? "text-rp-grey" : "text-gray-900"}`}>All Providers</h3>
        </div>
        <div className="overflow-x-auto">
          <table className={`min-w-full divide-y ${isDarkMode ? "divide-rp-separator" : "divide-gray-200"}`}>
            <thead className={isDarkMode ? "bg-rp-surface-sunken" : "bg-gray-50"}>
              <tr>
                <th
                  className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                    isDarkMode ? "text-rp-grey/70" : "text-gray-500"
                  }`}
                >
                  Provider
                </th>
                <th
                  className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                    isDarkMode ? "text-rp-grey/70" : "text-gray-500"
                  }`}
                >
                  Created
                </th>
                <th
                  className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                    isDarkMode ? "text-rp-grey/70" : "text-gray-500"
                  }`}
                >
                  Updated
                </th>
                <th
                  className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                    isDarkMode ? "text-rp-grey/70" : "text-gray-500"
                  }`}
                >
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className={`${isDarkMode ? "bg-rp-surface divide-rp-separator" : "bg-white divide-gray-200"} divide-y`}>
              {providers.map(provider => (
                <tr key={provider.id} className={isDarkMode ? "hover:bg-rp-surface-raised" : "hover:bg-gray-50"}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-10 w-10">
                        <div
                          className={`h-10 w-10 rounded-full flex items-center justify-center overflow-hidden ${
                            isDarkMode ? "bg-rp-surface-raised" : "bg-gray-100"
                          }`}
                        >
                          <img
                            src={getProviderLogoUrl(provider)}
                            alt={`${provider.name} logo`}
                            className="h-10 w-10 object-cover rounded-full"
                            onError={e => {
                              // Fallback to favicon if image fails to load
                              const target = e.target as HTMLImageElement;
                              target.src = "/favicon.png";
                            }}
                          />
                        </div>
                      </div>
                      <div className="ml-4">
                        <div className={`text-sm font-medium ${isDarkMode ? "text-rp-grey" : "text-gray-900"}`}>
                          {provider.name}
                        </div>
                        <div className={`text-sm ${isDarkMode ? "text-rp-grey/50" : "text-gray-500"}`}>
                          ID: {provider.id}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td
                    className={`px-6 py-4 whitespace-nowrap text-sm ${isDarkMode ? "text-rp-grey/50" : "text-gray-500"}`}
                  >
                    {new Date(provider.createdAt).toLocaleDateString()}
                  </td>
                  <td
                    className={`px-6 py-4 whitespace-nowrap text-sm ${isDarkMode ? "text-rp-grey/50" : "text-gray-500"}`}
                  >
                    {new Date(provider.updatedAt).toLocaleDateString()}
                  </td>
                  <td
                    className={`px-6 py-4 whitespace-nowrap text-sm ${isDarkMode ? "text-rp-grey/50" : "text-gray-500"}`}
                  >
                    <div className="flex space-x-2">
                      <button
                        onClick={() => navigate(`/admin/products?providerId=${provider.id}&type=basket`)}
                        className={`p-1 rounded transition-colors ${
                          isDarkMode
                            ? "text-green-400 hover:text-green-300 hover:bg-green-900/20"
                            : "text-green-600 hover:text-green-700 hover:bg-green-50"
                        }`}
                        title="View Baskets"
                      >
                        <List className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => navigate(`/admin/products?providerId=${provider.id}&type=portfolio`)}
                        className={`p-1 rounded transition-colors ${
                          isDarkMode
                            ? "text-purple-400 hover:text-purple-300 hover:bg-purple-900/20"
                            : "text-purple-600 hover:text-purple-700 hover:bg-purple-50"
                        }`}
                        title="View Portfolios"
                      >
                        <Briefcase className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => navigate(`/admin/providers/${provider.id}/subscriptions`)}
                        className={`p-1 rounded transition-colors ${
                          isDarkMode
                            ? "text-amber-400 hover:text-amber-300 hover:bg-amber-900/20"
                            : "text-amber-600 hover:text-amber-700 hover:bg-amber-50"
                        }`}
                        title="Manage Subscribers"
                      >
                        <Users className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => openEditModal(provider)}
                        className={`p-1 rounded transition-colors ${
                          isDarkMode
                            ? "text-blue-400 hover:text-blue-300 hover:bg-blue-900/20"
                            : "text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                        }`}
                        title="Edit provider"
                      >
                        <Edit3 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Provider Create Wizard */}
      <ProviderCreateWizard
        isOpen={wizardOpen}
        onClose={() => setWizardOpen(false)}
        onProviderCreated={async (provider: Provider) => {
          // Provider created callback (not used anymore)
        }}
        onRefreshProviders={fetchProviders}
      />

      {/* Provider Edit Modal */}
      <ProviderModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        provider={selectedProvider}
        mode={modalMode}
        onSave={handleEditProvider}
        onDelete={handleDeleteProvider}
      />
    </AdminPageLayout>
  );
};

export default ProvidersPage;
